"use client";

import { apiUrl, isElectron } from "@/src/shared/config/api";
import { getSession, hasBrowserSession } from "@/src/shared/browser/sessionStorage";
import {
  CONSENT_CHANGED_EVENT,
  HIPAA_DEVICE_HINT_KEY,
  getBrowserTelemetryComplianceMode,
} from "@/src/features/consent/browserConsent";
import { isBenignWindowError } from "@/src/shared/browser/benignWindowErrors";

const USER_LOG_SESSION_STORAGE_KEY = "nv-user-log-session";
const FRONTEND_DEV_LOG_STREAM_ENABLED = process.env.NEXT_PUBLIC_ENV === "development";
const DEV_LOG_STREAM_PATH = "/api/dev/frontend-logs";
const DEV_STREAM_MAX_QUEUE_LENGTH = 250;
const DEV_STREAM_MAX_BATCH_SIZE = 50;
const DEV_STREAM_FLUSH_INTERVAL_MS = 500;

// App logs for support reports. Recent console output and app events stay in
// this tab's memory only (never storage, never streamed) and leave the device
// only inside a support ticket or feedback report the person sends with
// "Attach app logs" on. On by default per device; HIPAA mode never captures.
// Local development additionally streams to the dev server's terminal.
export const TICKET_LOGS_KEY = "smartmanager_ticket_logs_v1";
export const TICKET_LOGS_CHANGED_EVENT = "smartmanager:ticket-logs-changed";
/** The former opt-in, 24-hour choice. Removed when read; the new default applies. */
const LEGACY_SUPPORT_CONSOLE_LOGS_KEY = "smartmanager_support_console_logs_v1";
/** Matches the server's per-ticket cap (MAX_SUPPORT_TICKET_LOG_BYTES). */
export const TICKET_LOG_MAX_BYTES = 256 * 1024;
export const TICKET_LOG_MAX_LINES = 500;
export const TICKET_LOG_MAX_LINE_BYTES = 4 * 1024;
const RAW_TEXT_SCAN_LIMIT = 16 * 1024;
const TRUNCATED_SUFFIX = " …[truncated]";
const MAX_REDACTED_OBJECT_KEYS = 50;
/** Key fragments that are unambiguous anywhere in a key, once separators are removed. */
const SENSITIVE_KEY_FRAGMENTS = /token|secret|passw(?:or)?d|passcode|cookie|authorization|apikey|privatekey|session|credential|jwt/;
/** Short words that are sensitive only as a whole key segment ("pin", not "spinner"; "auth", not "author"). */
const SENSITIVE_KEY_SEGMENTS: ReadonlySet<string> = new Set(["auth", "oauth", "otp", "pin", "refresh"]);

type StoredUserLogSession = {
  id: string;
  startedAt: number;
  device: string;
};

type BrowserLogEvent = {
  timestamp: number;
  level: "debug" | "info" | "warn" | "error";
  event: string;
  message?: string;
  path?: string;
  details?: unknown;
};

type TicketLogLine = { text: string; bytes: number };

let installed = false;
let volatileSession: StoredUserLogSession | null = null;
/** Holds an opt-out for this page when storage refuses to save it. */
let volatileOptOut = false;
/** The hot-path answer to "keep logs for tickets?", dropped whenever a choice or the HIPAA mode can change. */
let allowedCache: boolean | null = null;
let trackedWindow: Window | null = null;
let legacyChoiceRemoved = false;
let ticketLog: TicketLogLine[] = [];
let ticketLogBytes = 0;
let devStreamQueue: BrowserLogEvent[] = [];
let devStreamTimer: number | undefined;
let devStreamFlushing = false;
let encoder: TextEncoder | null = null;
const originalConsole: Partial<Record<"debug" | "info" | "log" | "warn" | "error", (...args: unknown[]) => void>> = {};

function randomId(): string {
  const randomPart =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `ulog_${Date.now().toString(36)}_${randomPart}`;
}

function classifyDevice(userAgent: string): string {
  if (/mobile|iphone|android.*mobile|windows phone/i.test(userAgent)) return "phone";
  if (/ipad|tablet|android/i.test(userAgent)) return "tablet";
  if (/electron/i.test(userAgent)) return "desktop-app";
  return "desktop";
}

function readStoredSession(): StoredUserLogSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(USER_LOG_SESSION_STORAGE_KEY);
    if (!raw) return volatileSession;
    const parsed = JSON.parse(raw);
    if (!parsed?.id || typeof parsed.startedAt !== "number" || !parsed.device) return volatileSession;
    return parsed;
  } catch {
    return volatileSession;
  }
}

function writeStoredSession(session: StoredUserLogSession): void {
  if (typeof window === "undefined") return;
  volatileSession = session;
  try {
    localStorage.setItem(USER_LOG_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Telemetry continuity is optional; denied storage must never block auth.
  }
}

/** A new sign-in starts a new log session; the previous person's app log is dropped. */
export function startNewUserLogSession(): StoredUserLogSession | null {
  if (typeof window === "undefined") return null;
  clearTicketLog();
  const session = {
    id: randomId(),
    startedAt: Date.now(),
    device: classifyDevice(navigator.userAgent || ""),
  };
  writeStoredSession(session);
  return session;
}

export function ensureUserLogSession(): StoredUserLogSession | null {
  return readStoredSession() || startNewUserLogSession();
}

export function getUserLogSessionHeaders(): Record<string, string> {
  const session = ensureUserLogSession();
  if (!session) return {};
  return {
    "x-user-log-session-id": session.id,
    "x-user-log-session-started-at": String(session.startedAt),
    "x-user-log-device": session.device,
  };
}

// Order matters: whole data URLs and tokens go before the email and query
// patterns could split them. Partial matches are covered too, because a line
// can be cut in the middle of a secret.
const TEXT_REDACTIONS: ReadonlyArray<readonly [RegExp, string]> = [
  [/data:([\w.+-]+\/[\w.+-]+);base64,[A-Za-z0-9+/=]*/gi, "data:$1;base64,[REDACTED]"],
  // Iron-sealed cookies (WorkOS sealed sessions): Fe26.2*<id>*<salt>*...
  [/\bFe26\.2\*[\w*~.-]*/g, "[REDACTED_SEALED]"],
  [/\beyJ[\w-]{6,}(?:\.[\w-]*){0,2}/g, "[REDACTED_JWT]"],
  // Stripe client secrets carry the object id: pi_..._secret_..., seti_..._secret_...
  [/\b(?:pi|seti)_[A-Za-z0-9]+_secret_[A-Za-z0-9]+/g, "[REDACTED_KEY]"],
  [/\b(Bearer|Basic)\s+[\w.~+/=-]+/gi, "$1 [REDACTED]"],
  [/\b(?:sk-(?:proj-|ant-)?[\w-]{12,}|[sr]k_(?:live|test)_[A-Za-z0-9]{8,}|whsec_[A-Za-z0-9]{16,}|gh[pousr]_[A-Za-z0-9]{16,}|github_pat_\w{16,}|xox[abprs]-[\w-]{8,})/g, "[REDACTED_KEY]"],
  [/[\w.%+-]+@[\w-]+(?:\.[\w-]+)*\.[A-Za-z]{2,}/g, "[REDACTED_EMAIL]"],
  [/([?&#;][\w.-]*(?:token|secret|passw(?:or)?d|key|session|auth|sig(?:nature)?|code|credential|state)[\w.-]*=)[^&#\s"'<>]+/gi, "$1[REDACTED]"],
];

/** Strips credentials, personal addresses and embedded files from free text. */
export function redactLogText(value: string): string {
  let text = value.length > RAW_TEXT_SCAN_LIMIT ? value.slice(0, RAW_TEXT_SCAN_LIMIT) : value;
  for (const [pattern, replacement] of TEXT_REDACTIONS) text = text.replace(pattern, replacement);
  return text;
}

/** Whether an object key names a credential or one-time secret. */
export function isSensitiveLogKey(key: string): boolean {
  if (SENSITIVE_KEY_FRAGMENTS.test(key.toLowerCase().replace(/[^a-z0-9]/g, ""))) return true;
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((segment) => SENSITIVE_KEY_SEGMENTS.has(segment));
}

function redact(value: unknown, depth = 0): unknown {
  if (depth > 4) return "[MaxDepth]";
  if (value == null) return value;
  if (typeof value === "string") {
    const text = redactLogText(value);
    return text.length > 2000 ? `${text.slice(0, 2000)}...[truncated]` : text;
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value instanceof Error) {
    return {
      name: value.name,
      message: redactLogText(value.message),
      ...(value.stack ? { stack: redactLogText(value.stack) } : {}),
    };
  }
  if (Array.isArray(value)) return value.slice(0, 20).map((item) => redact(item, depth + 1));
  if (typeof value === "object") {
    const output: Record<string, unknown> = {};
    const entries = Object.entries(value as Record<string, unknown>);
    for (const [key, nestedValue] of entries.slice(0, MAX_REDACTED_OBJECT_KEYS)) {
      output[key] = isSensitiveLogKey(key) ? "[REDACTED]" : redact(nestedValue, depth + 1);
    }
    if (entries.length > MAX_REDACTED_OBJECT_KEYS) output["…"] = `${entries.length - MAX_REDACTED_OBJECT_KEYS} more keys`;
    return output;
  }
  return redactLogText(String(value));
}

function serializeConsoleArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === "string") return redactLogText(arg);
      try {
        return JSON.stringify(redact(arg));
      } catch {
        return redactLogText(String(arg));
      }
    })
    .join(" ");
}

export type SupportConsoleLogState =
  | { status: "on" }
  | { status: "off" }
  | { status: "unavailable" };

function ticketLogOptedOut(): boolean {
  if (volatileOptOut) return true;
  try {
    if (!legacyChoiceRemoved) {
      window.localStorage.removeItem(LEGACY_SUPPORT_CONSOLE_LOGS_KEY);
      legacyChoiceRemoved = true;
    }
    return window.localStorage.getItem(TICKET_LOGS_KEY) === "off";
  } catch {
    return false;
  }
}

/** Whether this device attaches app logs to support reports. On unless the person turned it off. */
export function readSupportConsoleLogState(): SupportConsoleLogState {
  if (typeof window === "undefined") return { status: "off" };
  if (getBrowserTelemetryComplianceMode() === "hipaa") return { status: "unavailable" };
  return ticketLogOptedOut() ? { status: "off" } : { status: "on" };
}

/**
 * The cached answer for the console hot path. Reading storage and the HIPAA
 * hint on every console call is too slow, so the answer is kept in memory and
 * dropped when this tab's choice, another tab's choice, or the compliance mode
 * changes. Losing permission also drops what this tab holds.
 */
function ticketLogAllowed(): boolean {
  if (typeof window === "undefined") return false;
  if (trackedWindow !== window) trackAllowedState();
  allowedCache ??= readSupportConsoleLogState().status === "on";
  return allowedCache;
}

function refreshAllowedState(): void {
  allowedCache = null;
  if (!ticketLogAllowed()) clearTicketLog();
}

function trackAllowedState(): void {
  trackedWindow = window;
  allowedCache = null;
  window.addEventListener(TICKET_LOGS_CHANGED_EVENT, refreshAllowedState);
  window.addEventListener(CONSENT_CHANGED_EVENT, refreshAllowedState);
  window.addEventListener("storage", (event) => {
    if (event.key === TICKET_LOGS_KEY || event.key === HIPAA_DEVICE_HINT_KEY || event.key === null) {
      refreshAllowedState();
    }
  });
}

function clearTicketLog(): void {
  ticketLog = [];
  ticketLogBytes = 0;
}

/** Turns app logs for support reports on or off on this device. False when not allowed or not saved. */
export function setSupportConsoleLogSharing(enabled: boolean): boolean {
  if (typeof window === "undefined") return false;
  if (enabled && getBrowserTelemetryComplianceMode() === "hipaa") return false;
  let saved = true;
  try {
    if (enabled) window.localStorage.removeItem(TICKET_LOGS_KEY);
    else window.localStorage.setItem(TICKET_LOGS_KEY, "off");
  } catch {
    saved = false;
  }
  // Saved choices live in storage so other tabs follow them; memory only
  // covers an opt-out that storage refused.
  volatileOptOut = saved ? false : !enabled;
  allowedCache = null;
  if (!enabled) clearTicketLog();
  window.dispatchEvent(new CustomEvent(TICKET_LOGS_CHANGED_EVENT));
  return saved;
}

/** Notifies on this device's choice, another tab's choice, and a HIPAA mode change. */
export function subscribeToSupportConsoleLogs(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === TICKET_LOGS_KEY || event.key === null) listener();
  };
  window.addEventListener(TICKET_LOGS_CHANGED_EVENT, listener);
  window.addEventListener(CONSENT_CHANGED_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(TICKET_LOGS_CHANGED_EVENT, listener);
    window.removeEventListener(CONSENT_CHANGED_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}

function byteLength(text: string): number {
  encoder ??= new TextEncoder();
  return encoder.encode(text).byteLength;
}

function capLineBytes(text: string): TicketLogLine {
  encoder ??= new TextEncoder();
  const encoded = encoder.encode(text);
  if (encoded.byteLength <= TICKET_LOG_MAX_LINE_BYTES) return { text, bytes: encoded.byteLength };
  const room = TICKET_LOG_MAX_LINE_BYTES - byteLength(TRUNCATED_SUFFIX);
  // A cut inside a multi-byte character decodes to U+FFFD; drop it.
  const head = new TextDecoder().decode(encoded.subarray(0, room)).replace(/�+$/, "");
  const capped = `${head}${TRUNCATED_SUFFIX}`;
  return { text: capped, bytes: byteLength(capped) };
}

function formatTicketLogLine(entry: BrowserLogEvent): string {
  let body = entry.message ?? "";
  if (!body && entry.details !== undefined) {
    try {
      body = JSON.stringify(entry.details) ?? "";
    } catch {
      body = "[unserializable details]";
    }
  }
  const head = `${new Date(entry.timestamp).toISOString()} ${entry.level.toUpperCase()} ${entry.event}`;
  return [head, entry.path, body].filter(Boolean).join(" ").replace(/[\r\n]+/g, " ⏎ ");
}

function appendTicketLog(entry: BrowserLogEvent): void {
  const line = capLineBytes(formatTicketLogLine(entry));
  ticketLog.push(line);
  // Each line also costs its newline when joined.
  ticketLogBytes += line.bytes + 1;
  while (ticketLog.length > TICKET_LOG_MAX_LINES || ticketLogBytes > TICKET_LOG_MAX_BYTES) {
    const dropped = ticketLog.shift();
    if (!dropped) break;
    ticketLogBytes -= dropped.bytes + 1;
  }
}

/** UTF-8 size of a value once serialized as JSON, as it travels in a request body. */
export function jsonByteLength(value: unknown): number {
  return byteLength(JSON.stringify(value) ?? "");
}

/**
 * The redacted app log to attach to a support report, oldest line first, or
 * null when there is none or this device does not attach logs. Reading it
 * does not clear it, so a failed submit can be retried with the same log.
 *
 * `maxJsonBytes` bounds the log's size as a JSON string (quotes and escapes
 * included). The oldest lines go first; null when not even the newest fits.
 */
export function collectTicketLog(options: { maxJsonBytes?: number } = {}): string | null {
  if (typeof window === "undefined") return null;
  // Fresh read, not the hot-path cache: this is the moment the log leaves.
  allowedCache = null;
  if (!ticketLogAllowed()) {
    clearTicketLog();
    return null;
  }
  const { maxJsonBytes } = options;
  let lines = ticketLog;
  if (maxJsonBytes !== undefined) {
    // Two bytes for the quotes; each joined "\n" escapes to two more.
    let used = 2;
    let start = ticketLog.length;
    while (start > 0) {
      const cost = jsonByteLength(ticketLog[start - 1]!.text) - 2 + (start < ticketLog.length ? 2 : 0);
      if (used + cost > maxJsonBytes) break;
      used += cost;
      start -= 1;
    }
    lines = ticketLog.slice(start);
  }
  if (lines.length === 0) return null;
  return lines.map((line) => line.text).join("\n");
}

export function queueUserSessionLogEvent(
  level: BrowserLogEvent["level"],
  event: string,
  details?: unknown,
  message?: string,
): void {
  if (typeof window === "undefined") return;
  recordLogEvent(level, event, () => ({
    ...(message === undefined ? {} : { message: redactLogText(message) }),
    ...(details === undefined ? {} : { details: redact(details) }),
  }));
}

/**
 * Keeps one event when logs are kept for tickets or streamed in development.
 * The cheap checks run first and `describe` (redaction and serialization) runs
 * only when the event is kept.
 */
function recordLogEvent(
  level: BrowserLogEvent["level"],
  event: string,
  describe: () => Pick<BrowserLogEvent, "message" | "details">,
): void {
  const keepForTickets = ticketLogAllowed();
  // Consent first: in Electron the session check is a sync IPC round trip,
  // so the dev stream's session check runs only in local development.
  const streamToDevServer = FRONTEND_DEV_LOG_STREAM_ENABLED && hasBrowserSession();
  if (!keepForTickets && !streamToDevServer) return;

  const entry: BrowserLogEvent = {
    timestamp: Date.now(),
    level,
    event,
    ...describe(),
    path: window.location.pathname,
  };
  if (keepForTickets) appendTicketLog(entry);
  if (streamToDevServer) {
    devStreamQueue.push(entry);
    if (devStreamQueue.length > DEV_STREAM_MAX_QUEUE_LENGTH) {
      devStreamQueue = devStreamQueue.slice(devStreamQueue.length - DEV_STREAM_MAX_QUEUE_LENGTH);
    }
    scheduleDevStreamFlush();
  }
}

function scheduleDevStreamFlush(): void {
  if (devStreamTimer != null || typeof window === "undefined") return;
  devStreamTimer = window.setTimeout(() => {
    devStreamTimer = undefined;
    void flushUserSessionLogs();
  }, DEV_STREAM_FLUSH_INTERVAL_MS);
}

/** Local development only: sends queued events to the dev server's terminal. */
export async function flushUserSessionLogs(): Promise<void> {
  if (!FRONTEND_DEV_LOG_STREAM_ENABLED) return;
  if (typeof window === "undefined" || devStreamFlushing || devStreamQueue.length === 0) return;
  if (!hasBrowserSession()) return;
  const sessionId = getSession();

  const events = devStreamQueue.splice(0, DEV_STREAM_MAX_BATCH_SIZE);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...getUserLogSessionHeaders(),
  };
  if (sessionId) headers["x-session-id"] = sessionId;

  devStreamFlushing = true;
  try {
    const response = await fetch(apiUrl(DEV_LOG_STREAM_PATH), {
      method: "POST",
      headers,
      body: JSON.stringify({ events }),
      credentials: isElectron ? "include" : "same-origin",
      keepalive: true,
    });
    if (!response.ok) devStreamQueue = events.concat(devStreamQueue).slice(0, DEV_STREAM_MAX_QUEUE_LENGTH);
  } catch {
    devStreamQueue = events.concat(devStreamQueue).slice(0, DEV_STREAM_MAX_QUEUE_LENGTH);
  } finally {
    devStreamFlushing = false;
    if (devStreamQueue.length > 0) scheduleDevStreamFlush();
  }
}

export function installUserSessionLogCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  for (const method of ["debug", "info", "log", "warn", "error"] as const) {
    originalConsole[method] = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      originalConsole[method]?.(...args);
      // The serialized text is the whole record; it is built and redacted once,
      // and only when the line is kept.
      recordLogEvent(method === "log" ? "info" : method, "client.console", () => ({
        message: serializeConsoleArgs(args),
      }));
    };
  }

  window.addEventListener("error", (event) => {
    if (isBenignWindowError(event)) return;
    queueUserSessionLogEvent("error", "client.window_error", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error instanceof Error ? event.error : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    queueUserSessionLogEvent("error", "client.unhandled_rejection", {
      reason: event.reason instanceof Error ? event.reason : redact(event.reason),
    });
  });

  // Start tracking now, so an opt-out in another tab or a switch to HIPAA
  // mode drops what this tab holds even before the first console line.
  ticketLogAllowed();

  if (FRONTEND_DEV_LOG_STREAM_ENABLED) {
    window.addEventListener("pagehide", () => {
      void flushUserSessionLogs();
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void flushUserSessionLogs();
    });
  }
}
